import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { IconPlaylist, spacer4 } from '@spotify-internal/encore-creator-web';
import {
  numFormatting,
  numFollowersFormatting,
  playlistLinksOut,
  goToPlaylist,
} from '../../HomeHelper';
import * as Style from '../Style';
import StatsSubHeader from '../StatsSubHeader';
import StatsCTABtn from '../StatsCTABtn';

const PlaylistRow = styled(Style.StatsRowContainer)`
  position: relative; /* prevent algorithmic playlists from appearing grayed out  */
`;

const StyledLink = styled(Style.StatsTextLink)`
  &::after {
    /* url encoded encore-web IconArrowTopRight rendered in pseudo as opposed to separate container to prevent alignment issues */
    content: url('data:image/svg+xml, %3Csvg%20data-name%3D%22Icons%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%3E%3Cpath%20fill%3D%22none%22%20stroke%3D%22%234100f5%22%20stroke-width%3D%22.8pt%22%20vector-effect%3D%22non-scaling-stroke%22%20d%3D%22M5.47%2018.53L18.69%205.31M8%205.3l10.69.01.02%2010.69%22%2F%3E%3C%2Fsvg%3E');
    margin-left: ${spacer4};
    vertical-align: middle;
  }
`;

const TopPlaylists = React.memo(props => {
  const { topPlaylists } = props;

  const TopPlaylistsHeader = (
    <StatsSubHeader title="Your top playlists" dataTitle="streams" />
  );

  const AllPlaylistsBtn = (
    <StatsCTABtn
      title="See playlists"
      url={`/artist/${props.artist.id}/music/playlists?time-filter=7day`}
      action="playlists"
      label="goToPlaylists"
    />
  );

  if (topPlaylists.length > 0) {
    const sortedPlaylists = topPlaylists.sort((a, b) => {
      if (a.streams === b.streams) return 0;
      return a.streams > b.streams ? -1 : 1;
    });

    return (
      <div data-testid="tplaylists">
        {TopPlaylistsHeader}
        {sortedPlaylists.slice(0, 3).map(playlist => (
          <PlaylistRow
            data-testid="tplaylists-row"
            isDisabled={
              !playlistLinksOut(playlist.uri, playlist.isPersonalized)
            }
            key={playlist.uri}
            onClick={
              playlistLinksOut(playlist.uri, playlist.isPersonalized)
                ? () => goToPlaylist(playlist.uri)
                : null
            }
          >
            <Style.StatsRow>
              <Style.StatsMediaEntity>
                <Style.StatsMediaCover
                  imgSrc={playlist.thumbnailUrl}
                  size={48}
                  defaultIcon={<IconPlaylist iconSize={24} />}
                />
                <Style.StatsMediaInfo>
                  <Style.CenteredFlexDiv>
                    <Style.StatsMediaTitle>
                      {playlistLinksOut(
                        playlist.uri,
                        playlist.isPersonalized,
                      ) ? (
                        <StyledLink href="#">{playlist.title}</StyledLink>
                      ) : (
                        playlist.title
                      )}
                    </Style.StatsMediaTitle>
                  </Style.CenteredFlexDiv>
                  <Style.StatsDeltaTitle>
                    {numFollowersFormatting(playlist.followers)}
                  </Style.StatsDeltaTitle>
                </Style.StatsMediaInfo>
              </Style.StatsMediaEntity>
              <Style.StatsDeltaContainer>
                <Style.StatsDeltaTitle>
                  {numFormatting(playlist.streams)}
                </Style.StatsDeltaTitle>
              </Style.StatsDeltaContainer>
            </Style.StatsRow>
          </PlaylistRow>
        ))}
        {AllPlaylistsBtn}
      </div>
    );
  }

  return (
    <React.Fragment>
      {TopPlaylistsHeader}
      <Style.StatsEmptyText data-testid="empty-playlists">
        No stats for this time period. Stats appear the day after you get
        streams from a playlist.
      </Style.StatsEmptyText>
      {AllPlaylistsBtn}
    </React.Fragment>
  );
});

TopPlaylists.propTypes = {
  artist: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  topPlaylists: PropTypes.arrayOf(
    PropTypes.shape({
      followers: PropTypes.number,
      listeners: PropTypes.number,
      thumbnailUrl: PropTypes.string,
      uri: PropTypes.string,
      title: PropTypes.string,
      isPersonalized: PropTypes.bool,
    }),
  ),
};

/* eslint-disable-next-line import/no-default-export */
export default TopPlaylists;
